import { useEffect, useMemo, useRef } from "react";
import { NotificationsService } from "../services/notifications.service";
import { WarehouseNotificationsService } from "../services/warehouseNotifications.service";
import { connectSocket } from "../services/notificationsSocket";
import { getAuthSession, getAuthUser } from "../pages/AdminAuth/components/authStore";

const SOUND_SRC = "/faah.mp3";
const POLL_MS = 15000;
const MAX_SEEN_IDS = 120;
const SOUND_COOLDOWN_MS = 1800;

let audioInstance = null;
let lastPlayedAt = 0;
let unlockListenersAttached = false;
let unlocked = false;

function getAudio() {
  if (typeof window === "undefined") return null;

  if (!audioInstance) {
    audioInstance = new Audio(SOUND_SRC);
    audioInstance.preload = "auto";
    audioInstance.volume = 1;
  }

  return audioInstance;
}

function attachUnlockListeners() {
  if (typeof window === "undefined" || unlockListenersAttached) return;

  unlockListenersAttached = true;

  const unlock = async () => {
    const audio = getAudio();
    if (!audio) return;

    try {
      const oldVolume = audio.volume;
      audio.volume = 0;
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = oldVolume || 1;
      unlocked = true;
    } catch (_err) {
      // Browser did not allow autoplay yet. Next user gesture will retry.
    }
  };

  ["pointerdown", "keydown", "touchstart", "click"].forEach((eventName) => {
    window.addEventListener(eventName, unlock, { passive: true });
  });
}

async function playOrderAlertSound() {
  const now = Date.now();
  if (now - lastPlayedAt < SOUND_COOLDOWN_MS) return false;

  const audio = getAudio();
  if (!audio) return false;

  try {
    lastPlayedAt = now;
    audio.pause();
    audio.currentTime = 0;
    audio.volume = 1;
    await audio.play();
    unlocked = true;
    return true;
  } catch (err) {
    unlocked = false;
    console.warn("[order-sound] Browser blocked faah.mp3 until user interacts with the page:", err?.message || err);
    return false;
  }
}

function clean(value) {
  return String(value || "").trim();
}

function upper(value) {
  return clean(value).toUpperCase();
}

function getMeta(item = {}) {
  return item?.meta && typeof item.meta === "object" ? item.meta : {};
}

function getNotificationId(item = {}) {
  const meta = getMeta(item);
  return clean(
    item?.id ||
      item?.notificationId ||
      item?.notification_id ||
      item?.dedupeKey ||
      item?.dedupe_key ||
      meta?.notificationId ||
      meta?.notification_id ||
      [item?.type || item?.event_type, item?.entityId || item?.entity_id || meta?.orderId, item?.created_at || item?.createdAt]
        .filter(Boolean)
        .join(":")
  );
}

function getCreatedTime(item = {}) {
  const value = item?.created_at || item?.createdAt || item?.created || item?.timestamp;
  const ts = value ? new Date(value).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
}

function isUnread(item = {}) {
  return !(item?.is_read || item?.isRead);
}

function buildNotificationSearchText(item = {}) {
  const meta = getMeta(item);
  return [
    item?.type,
    item?.event_type,
    item?.eventType,
    item?.title,
    item?.body,
    item?.message,
    item?.description,
    item?.actionUrl,
    item?.action_url,
    item?.entityType,
    item?.entity_type,
    meta?.type,
    meta?.event_type,
    meta?.eventType,
    meta?.alertType,
    meta?.popupEventType,
    meta?.screen,
    meta?.module,
    meta?.orderId,
    meta?.orderNumber,
    meta?.paymentStatus,
    meta?.paymentMethod,
    meta?.deliveryAssignmentStatus,
    meta?.status,
    meta?.displayAction,
  ]
    .filter(Boolean)
    .map((value) => (typeof value === "object" ? JSON.stringify(value) : String(value)))
    .join(" ")
    .toLowerCase();
}

function isNewOrderSoundNotification(item = {}, source = "admin") {
  const type = upper(item?.type || item?.event_type || item?.eventType || getMeta(item)?.type);

  const warehouseNewOrderTypes = new Set([
    "WAREHOUSE_NEW_ORDER",
    "WAREHOUSE_NEW_ORDER_PLACED",
    "WAREHOUSE_ORDER_PLACED",
    "ORDER_PLACED",
  ]);

  const adminNewOrderTypes = new Set([
    "ADMIN_NEW_ORDER_PLACED",
    "WAREHOUSE_NEW_ORDER",
    "WAREHOUSE_NEW_ORDER_PLACED",
    "WAREHOUSE_ORDER_PLACED",
    "ORDER_PLACED",
  ]);

  const allowedTypes = source === "warehouse" ? warehouseNewOrderTypes : adminNewOrderTypes;

  // If backend provided an explicit notification type, trust it.
  // This deliberately prevents dispatch/reassign/payment/status notifications
  // from triggering the new-order sound even if their message text mentions an order.
  if (type) return allowedTypes.has(type);

  // Legacy fallback only for notifications that do not carry a type. Keep this
  // intentionally strict: the alert sound is reserved for a newly placed order.
  const text = buildNotificationSearchText(item);
  if (!text) return false;

  return (
    text.includes("new order placed") ||
    text.includes("new order received") ||
    text.includes("new customer order") ||
    text.includes("order has been placed")
  );
}

function getStorageKey(source, user, session) {
  const userKey = user?.id || user?.email || user?.adminId || "guest";
  const warehouseKey = session?.selectedWarehouseId || session?.warehouseAccess?.assignedWarehouseId || "all";
  return `faah_order_sound_seen_${source}_${userKey}_${warehouseKey}`;
}

function readSeenIds(key) {
  if (typeof window === "undefined") return new Set();

  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "[]");
    return new Set(Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : []);
  } catch {
    return new Set();
  }
}

function writeSeenIds(key, ids) {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(key, JSON.stringify([...ids].slice(-MAX_SEEN_IDS)));
  } catch {
    // ignore localStorage quota/private-mode errors
  }
}

function pickNotifications(response, source) {
  if (source === "warehouse") {
    return Array.isArray(response?.items) ? response.items : Array.isArray(response?.data) ? response.data : [];
  }

  return Array.isArray(response?.data) ? response.data : Array.isArray(response?.items) ? response.items : [];
}

async function fetchNotifications(source, user) {
  if (source === "warehouse") {
    return WarehouseNotificationsService.list({ page: 1, limit: 20 });
  }

  return NotificationsService.list(user, {
    isRead: false,
    limit: 20,
    offset: 0,
  });
}

export function useOrderNotificationSoundAlert({ source = "admin" } = {}) {
  const user = useMemo(() => getAuthUser() || {}, []);
  const session = useMemo(() => getAuthSession() || {}, []);
  const storageKey = useMemo(() => getStorageKey(source, user, session), [source, user, session]);
  const seenRef = useRef(readSeenIds(storageKey));
  const initializedRef = useRef(false);
  const latestSeenCreatedAtRef = useRef(0);
  const pollingRef = useRef(false);
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    attachUnlockListeners();
    getAudio();
  }, []);

  useEffect(() => {
    seenRef.current = readSeenIds(storageKey);
    initializedRef.current = false;
    latestSeenCreatedAtRef.current = 0;
  }, [storageKey]);

  useEffect(() => {
    let alive = true;

    async function scan({ playSound = true } = {}) {
      if (pollingRef.current) return;
      pollingRef.current = true;

      try {
        const response = await fetchNotifications(source, user);
        if (!alive) return;

        const list = pickNotifications(response, source)
          .filter(isUnread)
          .filter((item) => isNewOrderSoundNotification(item, source))
          .sort((a, b) => getCreatedTime(a) - getCreatedTime(b));

        const nextSeen = new Set(seenRef.current);
        const newItems = [];

        for (const item of list) {
          const id = getNotificationId(item);
          if (!id) continue;

          const createdAt = getCreatedTime(item);
          nextSeen.add(id);
          latestSeenCreatedAtRef.current = Math.max(latestSeenCreatedAtRef.current, createdAt);

          const isFreshAfterHookMounted = createdAt >= startedAtRef.current - 1500;

          if (!seenRef.current.has(id) && (initializedRef.current || isFreshAfterHookMounted)) {
            newItems.push(item);
          }
        }

        seenRef.current = nextSeen;
        writeSeenIds(storageKey, nextSeen);

        if (!initializedRef.current) {
          initializedRef.current = true;
        }

        if (playSound && newItems.length > 0) {
          await playOrderAlertSound();
        }
      } catch (err) {
        // Do not break dashboard if one polling request fails.
        console.warn(`[order-sound] ${source} notification scan failed:`, err?.message || err);
      } finally {
        pollingRef.current = false;
      }
    }

    scan({ playSound: true });
    const timer = window.setInterval(() => scan({ playSound: true }), POLL_MS);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [source, storageKey, user]);

  useEffect(() => {
    if (source !== "admin") return undefined;

    const socket = connectSocket();
    if (!socket) return undefined;

    const handleNotification = async (data) => {
      if (!isUnread(data) || !isNewOrderSoundNotification(data, source)) return;

      const id = getNotificationId(data);
      if (!id || seenRef.current.has(id)) return;

      seenRef.current.add(id);
      writeSeenIds(storageKey, seenRef.current);

      if (initializedRef.current) {
        await playOrderAlertSound();
      }
    };

    socket.on("notification", handleNotification);
    return () => socket.off("notification", handleNotification);
  }, [source, storageKey]);

  return {
    soundSrc: SOUND_SRC,
    soundUnlocked: unlocked,
  };
}
